import type {
  CampaignUtmJson,
  CampaignVisit,
} from '../../features/auth/models/auth.model';

export const CHANNEL_PARAM_KEY = 'cid';
const CHANNEL_MAX_LENGTH = 20;
const PAGE_PATH_MAX_LENGTH = 255;

/** Parámetros de anuncio que se guardan con la visita (UTM y los de Meta). */
const UTM_MAX_LENGTHS: Record<keyof CampaignUtmJson, number> = {
  utm_source: 100,
  utm_medium: 100,
  utm_campaign: 150,
  utm_term: 255,
  utm_content: 255,
  utm_id: 100,
  adset_id: 100,
  ad_id: 100,
  fbclid: 500,
};
const UTM_PARAM_KEYS = Object.keys(
  UTM_MAX_LENGTHS
) as (keyof CampaignUtmJson)[];

export type CampaignParams = Omit<CampaignVisit, 'visitante_uuid'>;

/** `cid` y parámetros de anuncio de la URL; `null` si no trae ninguno. */
export function readCampaignParams(
  params: URLSearchParams
): CampaignParams | null {
  return toCampaignParams(key => params.get(key));
}

export function stripCampaignParams(url: URL): URL {
  const next = new URL(url.href);
  next.searchParams.delete(CHANNEL_PARAM_KEY);
  for (const key of UTM_PARAM_KEYS) {
    next.searchParams.delete(key);
  }
  return next;
}

/** Visita pendiente guardada en cookie; `null` si no tiene la forma esperada. */
export function toPendingVisit(value: unknown): CampaignVisit | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  const uuid = record['visitante_uuid'];
  if (typeof uuid !== 'string' || !uuid) return null;
  const utm = record['utm_json'];
  const utmRecord =
    utm && typeof utm === 'object' ? (utm as Record<string, unknown>) : {};
  const params = toCampaignParams(key =>
    key === CHANNEL_PARAM_KEY ? record[key] : utmRecord[key]
  );
  if (!params) return null;
  const ruta =
    typeof record['ruta'] === 'string' ? pagePath(record['ruta']) : undefined;
  return { visitante_uuid: uuid, ...(ruta && { ruta }), ...params };
}

/** Ruta de la página, sin query. Vacío si no es una ruta del sitio. */
export function pagePath(pathname: string): string | undefined {
  const path = pathname.trim().slice(0, PAGE_PATH_MAX_LENGTH);
  if (!path.startsWith('/') || path.startsWith('//')) return undefined;
  if (path.includes('?') || path.includes('#') || path.includes('\\')) {
    return undefined;
  }
  return path;
}

export function sameVisit(a: CampaignVisit, b: CampaignVisit): boolean {
  return (
    a.visitante_uuid === b.visitante_uuid &&
    a.cid === b.cid &&
    a.ruta === b.ruta &&
    UTM_PARAM_KEYS.every(key => a.utm_json?.[key] === b.utm_json?.[key])
  );
}

function toCampaignParams(
  read: (key: string) => unknown
): CampaignParams | null {
  const cid = clean(read(CHANNEL_PARAM_KEY), CHANNEL_MAX_LENGTH);
  const utm: CampaignUtmJson = {};
  for (const key of UTM_PARAM_KEYS) {
    const value = clean(read(key), UTM_MAX_LENGTHS[key]);
    if (value) utm[key] = value;
  }
  const hasUtm = Object.keys(utm).length > 0;
  if (!cid && !hasUtm) return null;
  return {
    ...(cid && { cid }),
    ...(hasUtm && { utm_json: utm }),
  };
}

function clean(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== 'string') return undefined;
  return value.trim().slice(0, maxLength) || undefined;
}
